import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { currentUser } from '../../core/auth';
import { QueryParamsModel } from '../../core/_base/crud';
import { FieldSynonymLink, FieldTemplateMap } from '../../views/pages/forms-templates/models';
import {
  Standard,
  TransactionStandard,
  TransactionStandards,
} from '../../views/pages/compliance/models';
import { StatementStatus } from '../../statements-collections/enums';
import { ActivityType } from '../enums';
import {
  Activity,
  ActivityAttachment,
  ActivityComment,
  ActivityDocumentSaveBody,
  ActivityHistoryLog,
  ActivityTemplate,
  ActivityTemplateType,
  Catalog,
  FieldsLink,
  Step,
} from '../models';

@Injectable({ providedIn: 'root' })
export class ActivityTrackingService {
  private baseURL = environment.ACTIVITY_TRACKING_BASE;
  private companyId: number;

  constructor(private http: HttpClient, private store: Store) {
    this.store.select(currentUser).subscribe((user) => {
      this.companyId = user.userType === 'Super Admin' ? 0 : user.companyID;
    });
  }

  private static getActivityTemplateQueryString(queryParams: QueryParamsModel): string {
    const { pageNumber, pageSize, sortField, sortOrder, filter } = queryParams;
    let fromString = `page=${pageNumber}&size=${pageSize}&sort=${sortField},${sortOrder}`;

    if (filter.searchTerm) fromString += `&search_term=${filter.searchTerm}`;
    if (filter.id) fromString += `&id_has=${filter.id}`;
    if (filter.name) fromString += `&name_has=${filter.name}`;
    if (filter.docTemplateName)
      fromString += `&docmosis_template_name_has=${filter.docTemplateName}`;
    if (filter.typeId) fromString += `&activity_type_id=${filter.typeId}`;
    if (filter.notificationsOn) fromString += `&notifications_on=${filter.notificationsOn}`;
    if (filter.isGlobal) fromString += `&is_global=${filter.isGlobal}`;
    if (filter.isActive) fromString += `&is_active=${filter.isActive}`;

    return fromString;
  }

  private static getActivityQueryString(queryParams: QueryParamsModel): string {
    const { pageNumber, pageSize, sortField, sortOrder, filter } = queryParams;
    let fromString = `page=${pageNumber}&size=${pageSize}&sort=${sortField},${sortOrder}`;

    if (filter.searchTerm) fromString += `&search_term=${filter.searchTerm}`;
    if (filter.name) fromString += `&name_has=${filter.name}`;
    if (filter.type) fromString += `&type=${filter.type}`;
    if (filter.createdDate) fromString += `&created=${filter.createdDate}`;
    if (filter.dueDate) fromString += `&activity_due_date=${filter.dueDate}`;
    if (filter.entityId) fromString += `&entity_id=${filter.entityId}`;
    if (filter.status) {
      (Array.isArray(filter.status) ? filter.status : [filter.status]).forEach(
        (s) => (fromString += `&current_status=${s}`),
      );
    }

    return fromString;
  }

  private static getStatementActivityQueryString(queryParams: QueryParamsModel): string {
    const { pageNumber, pageSize, sortField, sortOrder, filter } = queryParams;
    let fromString = `page=${pageNumber}&size=${pageSize}&sort=${sortField},${sortOrder}`;

    if (filter.searchTerm) fromString += `&search_term=${filter.searchTerm}`;
    if (filter.jobId) fromString += `&job_id=${filter.jobId}`;
    if (filter.invoiceId) fromString += `&invoice_id=${filter.invoiceId}`;
    if (filter.uploadDate) fromString += `&upload_date=${filter.uploadDate}`;
    if (filter.dueDate) fromString += `&due_date=${filter.dueDate}`;
    if (filter.dueAmount) fromString += `&due_amount=${filter.dueAmount}`;
    if (filter.entityId) fromString += `&entity_id=${filter.entityId}`;
    if (filter.status) fromString += `&statement_status=${filter.status}`;

    return fromString;
  }

  getActivityTemplateTypes(): Observable<{ data: ActivityTemplateType[] }> {
    return this.http.get<{ data: ActivityTemplateType[] }>(`${this.baseURL}/types`);
  }
  getActivityTemplateAutocomplete(searchTerm: string): Observable<{ data: ActivityTemplate[] }> {
    return this.http.get<{ data: ActivityTemplate[] }>(
      `${this.baseURL}/companies/${this.companyId}/templates/autocomplete?search_term=${searchTerm}&is_active=true&notifications_on=true`,
    );
  }

  getActivityTemplateByID(id: number): Observable<any> {
    return this.http.get<any>(`${this.baseURL}/companies/${this.companyId}/templates/${id}`);
  }

  createActivityTemplate(
    activityTemplate: ActivityTemplate,
  ): Observable<{ data: ActivityTemplate }> {
    return this.http.post<{ data: ActivityTemplate }>(
      `${this.baseURL}/companies/${this.companyId}/templates`,
      activityTemplate,
    );
  }

  updateActivityTemplate(
    activityTemplate: ActivityTemplate,
  ): Observable<{ data: ActivityTemplate }> {
    return this.http.put<{ data: ActivityTemplate }>(
      `${this.baseURL}/companies/${this.companyId}/templates/${activityTemplate.id}/first-step`,
      activityTemplate,
    );
  }

  getActivityTemplateFieldSynonymLinks(
    activityTemplateId: number,
  ): Observable<{ data: { links: FieldSynonymLink[] } }> {
    const params = new HttpParams({
      fromString: `company_id=${this.companyId}&model_type=ACTIVITY_TEMPLATE&model_id=${activityTemplateId}&model_version=0`,
    });

    return this.http.get<{ data: { links: FieldSynonymLink[] } }>(
      `${environment.FORMS_CONCEPTS_BASE}/dictionaries/synonyms/links`,
      { params },
    );
  }

  saveActivityTemplateFieldsLink(
    activityTemplateId: number,
    fieldsLink: FieldsLink,
  ): Observable<void> {
    return this.http.put<void>(
      `${this.baseURL}/companies/${this.companyId}/templates/${activityTemplateId}/synonyms-filters`,
      fieldsLink,
    );
  }

  findCatalogsAutocomplete(
    activityTemplateTypeId: number,
    searchTerm: string,
  ): Observable<{ data: Catalog[] }> {
    const params = new HttpParams({
      fromString: `activity_type_id=${activityTemplateTypeId}&search_term=${searchTerm}`,
    });

    return this.http.get<{ data: Catalog[] }>(
      `${this.baseURL}/companies/${this.companyId}/catalogs/autocomplete`,
      { params },
    );
  }

  getActivityTemplateSteps(activityTemplateId: number): Observable<{ data: Step[] }> {
    return this.http.get<{ data: Step[] }>(
      `${this.baseURL}/companies/${this.companyId}/templates/${activityTemplateId}/steps`,
    );
  }

  saveActivityTemplateSteps(
    activityTemplateId: number,
    steps: Step[],
  ): Observable<{ data: Step[] }> {
    return this.http.put<{ data: Step[] }>(
      `${this.baseURL}/companies/${this.companyId}/templates/${activityTemplateId}/steps`,
      { steps },
    );
  }

  getActivityTemplateKeys(
    activityTemplateId: number,
    formId?: number,
  ): Observable<{ data: string[] }> {
    let fromString = '';

    if (formId) fromString += `form_id=${formId}`;

    const params = new HttpParams({ fromString });

    return this.http.get<{ data: string[] }>(
      `${this.baseURL}/companies/${this.companyId}/templates/${activityTemplateId}/keys`,
      { params },
    );
  }

  getActivityTemplateDocumentKeysMap(activityTemplateId: number): Observable<FieldTemplateMap> {
    const params = new HttpParams({
      fromString: `model_type=ACTIVITY_TEMPLATE&model_id=${activityTemplateId}`,
    });

    return this.http
      .get<{
        data: FieldTemplateMap[];
      }>(`${environment.FORMS_CONCEPTS_BASE}/companies/${this.companyId}/docmosis/links`, {
        params,
      })
      .pipe(map((res) => res?.data[0]));
  }

  saveActivityTemplateDocumentKeysMap(
    activityTemplateId: number,
    fieldTemplateMap: Partial<FieldTemplateMap>,
  ): Observable<void> {
    return this.http.put<void>(
      `${this.baseURL}/companies/${this.companyId}/templates/${activityTemplateId}/docmosis-link`,
      {
        model_docmosis_link: fieldTemplateMap,
      },
    );
  }

  getActivityTemplates(
    queryParams: QueryParamsModel,
  ): Observable<{ data: ActivityTemplate[]; count: number }> {
    const params = new HttpParams({
      fromString: ActivityTrackingService.getActivityTemplateQueryString(queryParams),
    });

    return this.http.get<{ data: ActivityTemplate[]; count: number }>(
      `${this.baseURL}/companies/${this.companyId}/templates`,
      { params },
    );
  }

  toggleActivityTemplateStatus(activityTemplate: ActivityTemplate): Observable<void> {
    return this.http.put<void>(
      `${this.baseURL}/companies/${this.companyId}/templates/${activityTemplate.id}/change-status`,
      {
        is_active: !activityTemplate.is_active,
      },
    );
  }

  getActivityTemplateComplianceStandards(activityTemplateId: number): Observable<Standard[]> {
    return this.http.get<Standard[]>(
      `${environment.COMPLIANCE_BASE}/companies/${this.companyId}/transactions/${activityTemplateId}?transaction_type=ACTIVITY_TEMPLATE`,
    );
  }

  getActivityTemplateStepsComplianceStandards(
    activityTemplateStepsIds: number[],
  ): Observable<TransactionStandard[]> {
    let fromString = 'transaction_type=ACTIVITY_TEMPLATE_STEP';

    activityTemplateStepsIds?.forEach((id) => {
      fromString += `&transaction_id=${id}`;
    });

    const params = new HttpParams({ fromString });

    return this.http.get<TransactionStandard[]>(
      `${environment.COMPLIANCE_BASE}/companies/${this.companyId}/transactions`,
      { params },
    );
  }

  saveActivityTemplateComplianceStandards(
    activityTemplateStandards: TransactionStandards[],
  ): Observable<void> {
    return this.http.put<void>(
      `${environment.COMPLIANCE_BASE}/companies/${this.companyId}/transactions/update-transactions-standards`,
      activityTemplateStandards,
    );
  }

  toggleActivityTemplateNotifications(
    activityTemplateId: number,
    turnedOn: boolean,
  ): Observable<void> {
    return this.http.put<void>(
      `${this.baseURL}/companies/${this.companyId}/templates/${activityTemplateId}/notifications-status`,
      {
        notifications_on: turnedOn,
      },
    );
  }

  getActivities(queryParams: QueryParamsModel): Observable<{ data: Activity[]; count: number }> {
    const params = new HttpParams({
      fromString: ActivityTrackingService.getActivityQueryString(queryParams),
    });

    return this.http.get<{ data: Activity[]; count: number }>(
      `${this.baseURL}/companies/${this.companyId}/activities`,
      { params },
    );
  }

  getActivity(activityId: number): Observable<{ data: Activity }> {
    return this.http.get<{ data: Activity }>(
      `${this.baseURL}/companies/${this.companyId}/activities/${activityId}`,
    );
  }

  getActivityAttachments(activityId: number): Observable<{ data: ActivityAttachment[] }> {
    return this.http.get<{ data: ActivityAttachment[] }>(
      `${this.baseURL}/companies/${this.companyId}/activities/${activityId}/attachments`,
    );
  }

  getActivityTypeProperties(activityType: ActivityType): Observable<{ data: string[] }> {
    return this.http.get<{ data: string[] }>(
      `${this.baseURL}/types/audited-properties?activity_type=${activityType}`,
    );
  }

  getActivityHistoryLogs(
    activityId: number,
    queryParams: QueryParamsModel,
  ): Observable<{ data: ActivityHistoryLog[] }> {
    const { filter } = queryParams;
    let fromString = '';

    if (filter.date) fromString += `&date=${filter.date}`;
    if (filter.user?.id) fromString += `&user_id=${filter.user.id}`;
    if (filter.property) fromString += `&property=${filter.property}`;

    const params = new HttpParams({ fromString });

    return this.http.get<{ data: ActivityHistoryLog[] }>(
      `${this.baseURL}/companies/${this.companyId}/activities/${activityId}/change-logs`,
      { params },
    );
  }

  createActivityComment(
    activityId: number,
    comment: string,
  ): Observable<{ data: ActivityComment }> {
    return this.http.post<{ data: ActivityComment }>(
      `${this.baseURL}/companies/${this.companyId}/activities/${activityId}/comments`,
      { comment },
    );
  }

  updateActivityComment(
    activityId: number,
    activityComment: ActivityComment,
  ): Observable<{ data: ActivityComment }> {
    return this.http.put<{ data: ActivityComment }>(
      `${this.baseURL}/companies/${this.companyId}/activities/${activityId}/comments/${activityComment.id}`,
      { comment: activityComment.comment },
    );
  }

  deleteActivityComment(activityId: number, commentId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.baseURL}/companies/${this.companyId}/activities/${activityId}/comments/${commentId}`,
    );
  }

  saveActivityDocument(activityId: number, saveBody: ActivityDocumentSaveBody): Observable<void> {
    return this.http.post<void>(
      `${this.baseURL}/companies/${this.companyId}/activities/${activityId}/generate-docmosis`,
      saveBody,
    );
  }

  getStatementsActivities(
    queryParams: QueryParamsModel,
  ): Observable<{ data: Activity[]; count: number }> {
    const params = new HttpParams({
      fromString: ActivityTrackingService.getStatementActivityQueryString(queryParams),
    });

    return this.http.get<{ data: Activity[]; count: number }>(
      `${this.baseURL}/companies/${this.companyId}/activities/statements`,
      { params },
    );
  }

  getCountsByJobStatus(): Observable<{
    data: { [key: string]: { count: number; amount: number } };
  }> {
    return this.http.get<{ data: { [key: string]: { count: number; amount: number } } }>(
      `${this.baseURL}/companies/${this.companyId}/activities/statements/job-counts`,
    );
  }

  getCountsByStatementStatus(
    jobId?: number,
  ): Observable<{ data: { [key: string]: { count: number; amount: number } } }> {
    let fromString = '';

    if (jobId) fromString += `job_id=${jobId}`;

    const params = new HttpParams({ fromString });

    return this.http.get<{ data: { [key: string]: { count: number; amount: number } } }>(
      `${this.baseURL}/companies/${this.companyId}/activities/statements/counts`,
      { params },
    );
  }

  releaseStatements(body: {
    job_id: number;
    statement_ids: number[];
    statement_statuses?: StatementStatus[];
  }): Observable<void> {
    return this.http.put<void>(
      `${this.baseURL}/companies/${this.companyId}/activities/statements/release`,
      body,
    );
  }

  cancelStatements(body: {
    job_id: number;
    statement_ids: number[];
    statement_statuses?: StatementStatus[];
  }): Observable<void> {
    return this.http.put<void>(
      `${this.baseURL}/companies/${this.companyId}/activities/statements/cancel`,
      body,
    );
  }

  getStatementsCollectionsConfigs(): Observable<{ data: { configs: { [key: string]: string } } }> {
    return this.http.get<{ data: { configs: { [key: string]: string } } }>(
      `${this.baseURL}/companies/${this.companyId}/activities/statements/configs`,
    );
  }

  saveStatementsCollectionsConfigs(configs: { [key: string]: string }): Observable<void> {
    return this.http.put<void>(
      `${this.baseURL}/companies/${this.companyId}/activities/statements/configs`,
      { configs },
    );
  }

  getStatementDocument(
    statementId: number,
  ): Observable<{ data: { id: number; path: string; url: string } }> {
    return this.http.get<{ data: { id: number; path: string; url: string } }>(
      `${this.baseURL}/companies/${this.companyId}/activities/statements/${statementId}/get-document`,
    );
  }

  generateStatementDocument(statementId: number): Observable<{ data: { result_base64: string } }> {
    return this.http.get<{ data: { result_base64: string } }>(
      `${this.baseURL}/companies/${this.companyId}/activities/statements/${statementId}/generate-document`,
    );
  }
}
